import { create } from "zustand";
import { auth } from '../../../../auth';
import convex, { waitForConvexAuthReady } from "../../../../convexClient";
import { api } from "../../../../convex/_generated/api";
import { withActiveTenant } from "../../../../utils/tenantUtils";
import { getBrowserTimeZone } from "../../../../utils/timeZone";

const normalizeTask = (doc) => ({
  id: doc._id || doc.id,
  name: doc?.name ?? "",
  description: doc?.description ?? "",
  assignedTo: doc?.assignedTo ?? "",
  dueDate: doc?.dueDate ?? "",
  status: doc?.status ?? "Pending",
  priority: doc?.priority ?? "Medium",
  visibility: doc?.visibility ?? "Team",
  initiativeName: doc?.initiativeName ?? "",
  ownerType: doc?.ownerType ?? "personal",
  groupId: doc?.groupId ?? null,
  assignedMembers: Array.isArray(doc?.assignedMembers) ? doc.assignedMembers : [],
  eventId: doc?.eventId ?? null,
  startTime: doc?.startTime ?? "",
  endTime: doc?.endTime ?? "",
  eventStartDate: doc?.eventStartDate ?? "",
  eventEndDate: doc?.eventEndDate ?? "",
  recurrence: doc?.recurrence ?? null,
  userId: doc?.userId,
  archived: doc?.archived ?? false,
  metadata: {
    userId: doc?.metadata?.userId,
    createdAt: doc?.metadata?.createdAt ?? null,
    updatedAt: doc?.metadata?.updatedAt ?? null,
  },
  sharedWith: Array.isArray(doc?.sharedWith) ? doc.sharedWith : [],
});

const TASK_UPDATE_FIELDS = [
  "name",
  "description",
  "assignedTo",
  "dueDate",
  "status",
  "priority",
  "visibility",
  "initiativeName",
  "sharedWith",
  "ownerType",
  "groupId",
  "assignedMembers",
  "createLinkedEvent",
  "startTime",
  "endTime",
  "eventStartDate",
  "eventEndDate",
  "recurrence",
  "archived",
];

const normalizeTaskUpdateArgs = (updates) => {
  const normalized = Object.fromEntries(
    TASK_UPDATE_FIELDS
      .filter((field) => Object.prototype.hasOwnProperty.call(updates, field))
      .map((field) => [field, updates[field]])
  );

  if (normalized.name !== undefined) {
    normalized.name = normalized.name?.trim() ?? "";
  }

  if (normalized.groupId == null) {
    delete normalized.groupId;
  }

  return normalized;
};

const useTaskStore = create((set, get) => ({
  tasks: [],
  loading: false,
  error: null,

  fetchTasks: async () => {
    const user = auth.currentUser;
    if (!user) { set({ tasks: [], error: "Not authenticated" }); return; }
    set({ loading: true, error: null });
    try {
      await waitForConvexAuthReady();
      const docs = await convex.query(api.tasks.listTasks, withActiveTenant());
      set({ tasks: docs.map(normalizeTask), loading: false });
    } catch (e) {
      set({ loading: false, error: e.message || "Failed to fetch tasks" });
    }
  },

  createTask: async (payload) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");

    const baseArgs = {
      name: payload.name?.trim() ?? "",
      description: payload.description ?? "",
      assignedTo: payload.assignedTo ?? "",
      dueDate: payload.dueDate ?? "",
      status: payload.status ?? "Pending",
      priority: payload.priority ?? "Medium",
      visibility: payload.visibility ?? "Team",
      initiativeName: payload.initiativeName ?? "",
      sharedWith: Array.isArray(payload.sharedWith) ? payload.sharedWith : [],
      ownerType: payload.ownerType ?? "personal",
      ...(payload.groupId ? { groupId: payload.groupId } : {}),
      ...(Array.isArray(payload.assignedMembers) ? { assignedMembers: payload.assignedMembers } : {}),
    };

    let id;
    let eventId = null;
    if (payload.createLinkedEvent && payload.dueDate && payload.startTime && payload.endTime) {
      // createTaskWithEvent returns { taskId, eventId }
      const result = await convex.mutation(api.tasks.createTaskWithEvent, withActiveTenant({
        ...baseArgs,
        startTime: payload.startTime,
        endTime: payload.endTime,
        ...(payload.eventStartDate ? { eventStartDate: payload.eventStartDate } : {}),
        ...(payload.eventEndDate ? { eventEndDate: payload.eventEndDate } : {}),
        ...(payload.recurrence !== null && payload.recurrence !== undefined ? { recurrence: payload.recurrence } : {}),
        timeZone: getBrowserTimeZone(),
      }));
      id = result.taskId;
      eventId = result.eventId ?? null;
    } else {
      id = await convex.mutation(api.tasks.createTask, withActiveTenant(baseArgs));
    }

    const newTask = normalizeTask({
      _id: id,
      ...payload,
      eventId,
      userId: user.uid,
      metadata: { userId: user.uid, createdAt: Date.now(), updatedAt: Date.now() },
    });

    set((s) => ({ tasks: [newTask, ...s.tasks] }));
    return id;
  },

  updateTask: async (id, updates) => {
    const user = auth.currentUser;
    if (!user) throw new Error("Not authenticated");
    const normalizedUpdates = normalizeTaskUpdateArgs(updates);

    const updatedTask = await convex.mutation(api.tasks.updateTask, {
      id,
      ...normalizedUpdates,
      timeZone: getBrowserTimeZone(),
    });

    set((s) => ({
      tasks: s.tasks.map((t) =>
        t.id === id
          ? normalizeTask(updatedTask ?? { ...t, ...normalizedUpdates })
          : t
      ),
    }));
  },

  archiveTask: async (id, archived = true) => {
    await convex.mutation(api.tasks.updateTask, { id, archived });
    set((s) => ({
      tasks: s.tasks.map((t) => (t.id === id ? { ...t, archived } : t)),
    }));
  },

  deleteTask: async (id) => {
    await convex.mutation(api.tasks.deleteTask, { id });
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));
  },
}));

export default useTaskStore;
